import { BeachType, HotelFeature } from '@/entities/hotel';
import type { SearchProgressStage, SerpCardDistance, SerpSort } from '../model/types';

export const SERP_LAYOUT_TEXTS = {
  back: 'Назад',
  chat: 'Чат',
  editSearch: 'Изменить параметры поиска',
  titleFallback: 'Поиск туров',
  summaryJoin: ', ',
  nightsDash: '–',
  flexSign: '±',
} as const;

export const TOURISTS_PLURAL_FORMS = ['турист', 'туриста', 'туристов'];

export const SERP_CARD_TEXTS = {
  renovation: 'реновация',
  opened: 'открытие',
  yearSuffix: 'г',
  priceFrom: 'от',
  locationJoin: ', ',
  photoAlt: 'Фото отеля',
  beachLineSuffix: '-я линия',
  allFeatures: 'Все особенности отеля',
  ordersPeriod: 'в месяц',
  reviewsEmpty: 'Нет отзывов',
  bookingJoin: ', ',
  bookingCheckIn: 'с',
  bookingNights: 'на',
  fewPlaces: 'Мало мест',
  select: 'Показать туры',
  meters: 'м',
  kilometers: 'км',
} as const;

export const SERP_CARD_DISTANCE_LABELS: Record<SerpCardDistance, string> = {
  airport: 'до аэропорта',
  beach: 'до пляжа',
  center: 'до центра',
};

/** Дата заезда в строке брони: «25 авг.» — день без ведущего нуля и сокращённый месяц. */
export const SERP_CARD_CHECK_IN_FORMAT = 'd MMM';

export const REVIEWS_PLURAL_FORMS = ['отзыв', 'отзыва', 'отзывов'];

export const ORDERS_PLURAL_FORMS = ['бронь', 'брони', 'броней'];

export const ROOMS_PLURAL_FORMS = ['номер', 'номера', 'номеров'];

export const NIGHTS_PLURAL_FORMS = ['ночь', 'ночи', 'ночей'];

export const SERP_CARD_FEATURE_LABELS: Record<HotelFeature, string> = {
  [HotelFeature.AirConditioner]: 'Кондиционер',
  [HotelFeature.Wifi]: 'Wi-Fi',
  [HotelFeature.NearTheBeach]: 'Близко к пляжу',
  [HotelFeature.NearTheLift]: 'Близко к подъемнику',
  [HotelFeature.NearTheCenter]: 'Близко к центру',
  [HotelFeature.WithKids]: 'Отдых с детьми',
  [HotelFeature.ForCouples]: 'Идеально для пар',
  [HotelFeature.AdultsOnly]: 'Только для взрослых',
  [HotelFeature.Wellness]: 'Оздоровительные туры',
};

export const SERP_CARD_BEACH_TYPE_LABELS: Record<BeachType, string> = {
  [BeachType.SandBeach]: 'Песок',
  [BeachType.SandPebbleBeach]: 'Песок, галька',
  [BeachType.PebbleBeach]: 'Галька',
};

export const SERP_FEATURES_SHEET_TEXTS = {
  title: 'Особенности отеля',
  close: 'Понятно',
} as const;

export const SERP_PROGRESS_TEXTS: Record<SearchProgressStage, string> = {
  operators: 'Ждём ответа от туроператоров...',
  hotels: 'Загружаем информацию об отелях...',
  prices: 'Актуализируем цены...',
};

export const SERP_PROGRESS_LABEL = 'Идёт поиск';

export const SERP_QUICK_FILTERS_TEXTS = {
  allFilters: 'Все фильтры',
  sort: 'Сортировка',
  selectedCount: 'выбрано',
  clear: 'Сбросить фильтр',
  countSeparator: ' · ',
} as const;

export const SERP_SORT_TEXTS = {
  title: 'Сортировка',
  apply: 'Применить',
} as const;

export const SERP_SORT_LABELS: Record<SerpSort, string> = {
  popular: 'Популярные',
  priceAsc: 'Сначала дешёвые',
  priceDesc: 'Сначала дорогие',
  rating: 'По оценкам',
};

export const SERP_FILTERS_TEXTS = {
  title: 'Фильтры',
  resetAll: 'Сбросить всё',
  apply: 'Применить',
  close: 'Закрыть',
  ratingAnyBadge: 'All',
  tooltipClose: 'Понятно',
  emiratesLabel: 'Туры на',
  emiratesLogo: 'Emirates',
} as const;

export const SERP_BUDGET_FILTER_TEXTS = {
  selectedPrefix: 'Бюджет:',
  from: 'от',
  upTo: 'до',
  currency: '₽',
  min: 'Минимум',
  max: 'Максимум',
  priceFor: 'Цена за',
} as const;

export const SERP_RESORTS_FILTER_TEXTS = {
  sheetTitle: 'Курорты',
  back: 'Назад',
  showAll: 'Все курорты',
  searchPlaceholder: 'Поиск по курортам',
  groupTitles: { popular: 'Популярные направления', other: 'Остальные направления' },
  allInCountry: (countryName: string): string => (countryName ? `Все курорты, ${countryName}` : 'Все курорты'),
} as const;

export const SERP_OPERATORS_FILTER_TEXTS = {
  showAll: 'Все туроператоры',
} as const;

export const SERP_PREPAYMENTS_FILTER_TEXTS = {
  optionSuffix: 'предоплата',
} as const;

export const BUDGET_TOURISTS_PLURAL_FORMS = ['туриста', 'туристов', 'туристов'];

export const SERP_MAP_TEXTS = {
  mapButton: 'На карте',
} as const;

export const SERP_ALTERNATIVE_SEARCH_TEXTS = {
  departureCityMessagePrefix: 'Не нашли туры с вылетом из города: ',
  similarMessage: 'Не нашли туры по вашим критериям. Ниже подобрали похожие предложения',
  notFoundMessage: 'Нет доступных туров. Рекомендуем изменить критерии поиска',
  departureCityTitlePrefix: 'Туры/отели из города: ',
  departureCityHint: 'Если город вылета вам не подходит, выберите другой город из списка:',
  similarTitle: 'Похожие предложения',
  leadTitle: 'Не нашли подходящий отель? Эксперт подберёт варианты — есть предложения не из выдачи',
  leadSubtitle: 'Наши сотрудники помогут найти лучший вариант',
  leadButton: 'Подобрать тур с экспертом',
} as const;

export const SERP_SHORT_LEAD_BANNER_TEXTS = {
  title: 'Подбираете отель? Наши сотрудники помогут найти лучший вариант',
  subtitle: 'Наши сотрудники помогут найти лучший вариант',
  button: 'Подберите мне отель',
} as const;

export const SERP_ALTERNATIVE_SEARCH_SUMMARY_TEXTS = {
  join: ', ',
  checkInPrefix: 'с ',
  checkOutJoin: ' по ',
  flexSign: '±',
  nightsPrefix: 'на ',
  touristsJoin: ' ',
} as const;

export const SERP_FILTERS_RESET_TEXTS = {
  title: 'Часть фильтров сняли',
  descriptionPrefix: 'С вашими фильтрами туров не нашлось. Мы сняли ',
  descriptionSuffix: ', чтобы показать близкие варианты',
  labelOpenQuote: '«',
  labelCloseQuote: '»',
  labelsJoin: ', ',
  labelsLastJoin: ' и ',
  restore: 'Вернуть фильтры',
  newCountryDescription:
    'Вы перешли в другую страну, и мы сняли заданные фильтры, чтобы сбросить неподходящее и показать больше вариантов',
} as const;

export const SERP_SEARCH_EXPIRED_TEXTS = {
  title: 'Результаты поиска устарели',
  description: 'Чтобы получить свежие предложения и актуальные цены — пожалуйста, обновите результаты поиска',
  refresh: 'Обновить',
  home: 'На главную',
} as const;
